import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Logo from "../components/Logo";

export default function Signup() {
    const nav = useNavigate();
    const { setUser } = useAuth();
    const [form, setForm] = useState({ name: "", email: "", password: "", age: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem("sd_waiver_accepted") !== "true") {
            nav("/age-gate");
        }
    }, [nav]);

    const googleSignup = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        const age = parseInt(form.age, 10);
        if (!age || age < 18) {
            toast.error("You must be 18 or older.");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/register", {
                email: form.email,
                password: form.password,
                name: form.name,
                age,
                waiver_accepted: true,
                dob_confirmed_18: true,
            });
            setUser(data.user);
            sessionStorage.removeItem("sd_waiver_accepted");
            toast.success("Account created. Let's set up your profile.");
            nav("/onboarding");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Signup failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col px-6 pt-8 pb-8 text-white">
            <button
                onClick={() => nav("/waiver")}
                data-testid="signup-back"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/50 hover:text-white w-fit"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>

            <div className="flex-1 flex flex-col justify-center">
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <Logo size="md" />
                    <h1 className="font-display text-5xl mt-6 leading-none">
                        CREATE<br /><span className="text-smash">ACCOUNT.</span>
                    </h1>
                    <p className="text-white/60 text-sm mt-2">It takes 30 seconds. Then we move.</p>
                </motion.div>

                <form onSubmit={submit} className="mt-6 space-y-3" data-testid="signup-form">
                    <input
                        type="text"
                        placeholder="Display name"
                        required
                        data-testid="signup-name-input"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full h-12 bg-ink-800 border border-white/10 focus:border-smash px-3 text-white outline-none"
                    />
                    <input
                        type="email"
                        placeholder="Email"
                        required
                        data-testid="signup-email-input"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full h-12 bg-ink-800 border border-white/10 focus:border-smash px-3 text-white outline-none"
                    />
                    <input
                        type="number"
                        placeholder="Age (18+)"
                        min="18"
                        max="120"
                        required
                        data-testid="signup-age-input"
                        value={form.age}
                        onChange={(e) => setForm({ ...form, age: e.target.value })}
                        className="w-full h-12 bg-ink-800 border border-white/10 focus:border-smash px-3 text-white outline-none"
                    />
                    <input
                        type="password"
                        placeholder="Password (min 8 chars)"
                        required
                        minLength={8}
                        data-testid="signup-password-input"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="w-full h-12 bg-ink-800 border border-white/10 focus:border-smash px-3 text-white outline-none"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="signup-submit-btn"
                        className="w-full h-14 bg-smash hover:bg-smash/90 disabled:opacity-50 text-white font-display text-lg tracking-wider uppercase transition-colors mt-2"
                    >
                        {loading ? "Creating…" : "Create Account"}
                    </button>
                </form>

                <div className="my-5 flex items-center gap-3">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                </div>

                <button
                    onClick={googleSignup}
                    data-testid="signup-google-btn"
                    className="w-full h-12 bg-white hover:bg-white/90 text-black font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Continue with Google
                </button>

                <p className="text-center text-xs text-white/50 mt-5">
                    Already have one? <Link to="/login" className="text-dash underline" data-testid="signup-to-login-link">Sign In</Link>
                </p>
            </div>
        </div>
    );
}
